import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { format } from "date-fns";
import { X } from "lucide-react";
import { useLocalStorage } from "usehooks-ts";

import { Button } from "components/ui/Button";
import { Pill } from "components/ui/Pill";
import { CdmReviewReferences } from "features/cdm/components/CdmReviewReferences";
import { DiscardFormModal } from "features/cdm/components/DiscardFormModal/DiscardFormModal";
import { ReviewAndExamForm } from "features/cdm/components/ReviewAndExamForm/ReviewAndExamForm";
import { Steps } from "features/cdm/components/Steps/Steps";
import { views } from "features/cdm/hooks/useView";
import { buildClinicalPrefill } from "features/cdm/utils/clinicalDataPrefill";
import { extractSubmissionError } from "features/cdm/utils/extractSubmissionError";
import { usePatientBaseline } from "features/patients/hooks/usePatientBaseline";
import { usePatientClinicalData } from "hooks/usePatientClinicalData";
import { formatCdmStatus,getCdmStatusColors } from "utils/cdmStatus";

import { OutcomeForm } from "./components/OutcomeForm/OutcomeForm";
import { RiskAndAssessmentForm } from "./components/RiskAndAssessmentForm/RiskAndAssessmentForm";
import { TestResultsForm } from "./components/TestResultsForm/TestResultsForm";
import { useSendOCF } from "./hooks/useSendOCF";
import { useValidateOCF } from "./hooks/useValidateOCF";
import style from "./OCFItemForm.module.css";

// Tests precedes Risk & Assessment so the QRisk3 calculator (in Risk &
// Assessment) can prefill from the bloods entered on the Tests step.
const OCF_STEPS = ["Review & Exam", "Tests", "Risk & Assessment", "Outcome"];

export const OCFItemForm = ({ item, setView, patientId, removeItem }) => {
  const [draft, setDraft, removeDraft] = useLocalStorage(
    `kinesin-ocf-draft-${patientId}-${item?.id}`,
    { data: item, activeStep: 1 }
  );

  const { data, activeStep } = draft;
  const setData = (updater) =>
    setDraft((prev) => ({
      ...prev,
      data: typeof updater === "function" ? updater(prev.data) : updater,
    }));
  const setActiveStep = (step) =>
    setDraft((prev) => ({ ...prev, activeStep: step }));

  const [isDiscardModalOpen, setIsDiscardModalOpen] = useState(false);

  const { data: baselineData } = usePatientBaseline(patientId);
  const { data: clinicalData } = usePatientClinicalData(patientId);

  const { mutateAsync: validateOCF } = useValidateOCF({ patientId });
  const { mutateAsync: sendOCF, isPending } = useSendOCF({
    patientId,
  });

  useEffect(() => {
    if (!item) {
      setView({ name: views.noChronicDiseases, cdmId: null });
    }
  }, [item, setView]);

  useEffect(() => {
    if (!clinicalData) return;

    const { bloods, vitals, diagnostics, lifestyle } =
      buildClinicalPrefill(clinicalData);

    setData((prev) => {
      const merged = {};

      // OCF fields are not matrix-gated; prefill any returned value that the
      // user has not already set (a user edit in `prev` always wins).
      for (const group of [bloods, vitals, diagnostics, lifestyle]) {
        for (const [field, value] of Object.entries(group)) {
          if (!prev[field]) merged[field] = value;
        }
      }

      return Object.keys(merged).length > 0 ? { ...prev, ...merged } : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clinicalData]);

  if (!item) {
    return null;
  }

  return (
    <section className={style.wrapper}>
      <div className={style.header}>
        <div className={style.headerInner}>
          <h3>{item.name}</h3>
          <p>{format(new Date(item.date), "dd-MM-yyyy")}</p>
        </div>
        <div className={style.statusWrapper}>
          <Pill
            color={getCdmStatusColors(item.status).color}
            bg={getCdmStatusColors(item.status).bg}
            border={getCdmStatusColors(item.status).border}
          >
            {formatCdmStatus(item.status)}
          </Pill>
          <Button
            variant="outlineGray"
            iconButton
            onClick={() => setIsDiscardModalOpen(true)}
            aria-label="Discard form"
          >
            <X size={16} />
          </Button>
        </div>
      </div>
      {isDiscardModalOpen && (
        <DiscardFormModal
          onClose={() => setIsDiscardModalOpen(false)}
          onConfirm={() => {
            removeDraft();
            removeItem();
          }}
        />
      )}
      <Steps steps={OCF_STEPS} activeStep={activeStep} />
      <CdmReviewReferences patientId={patientId} />
      {activeStep === 1 && (
        <ReviewAndExamForm
          data={data}
          isOCF={true}
          baselineData={baselineData}
          onContinue={(data) => {
            setData((prev) => ({ ...prev, ...data }));
            setActiveStep(activeStep + 1);
          }}
        />
      )}
      {activeStep === 2 && (
        <TestResultsForm
          data={data}
          onContinue={(newData) => {
            setData((prev) => ({ ...prev, ...newData }));
            setActiveStep(activeStep + 1);
          }}
          onBack={() => setActiveStep(activeStep - 1)}
        />
      )}
      {activeStep === 3 && (
        <RiskAndAssessmentForm
          onBack={() => setActiveStep(activeStep - 1)}
          data={data}
          onContinue={async (newData) => {
            // OCF validation runs here rather than on the Tests step: it needs the
            // QRisk3 collected on this Risk & Assessment step, which follows Tests
            // after the KIN-1231 reorder. It also yields the suggested outcome the
            // Outcome step consumes.
            const newDataToSend = { ...data, ...newData };
            const res = await validateOCF(newDataToSend);

            if (res.isValid) {
              setActiveStep(activeStep + 1);
              setData({
                ...newDataToSend,
                ocfSuggestedOutcomeReason: res.suggestedOutcome.reason,
                ocfSuggestedOutcome: res.suggestedOutcome.outcome,
                outcome: res.suggestedOutcome.outcome || "NORMAL",
              });
            } else {
              toast.error(
                res.validationErrors?.length > 0
                  ? res.validationErrors.join(", ")
                  : "Something went wrong"
              );
            }
          }}
        />
      )}
      {activeStep === 4 && (
        <OutcomeForm
          data={data}
          onBack={() => setActiveStep(activeStep - 1)}
          isLoading={isPending}
          onContinue={async (newData) => {
            const dataToSend = { ...data, ...newData };

            try {
              const res = await sendOCF(dataToSend);

              if (res?.success) {
                toast.success("OCF sent successfully");
                removeDraft();
                removeItem();
              } else {
                toast.error(
                  extractSubmissionError(res, "Error while sending OCF")
                );
              }
            } catch (error) {
              toast.error(
                extractSubmissionError(error, "Error while sending OCF")
              );
            }
          }}
        />
      )}
    </section>
  );
};
